import { Button } from "@/components/ui/button";
import { Zap, Shield, Brain, Database } from "lucide-react";
import { Link } from "react-router-dom";

interface LandingPageProps {
  onGetStarted: () => void;
  onSignIn: () => void;
}

const LandingPage = ({ onGetStarted, onSignIn }: LandingPageProps) => {
  return (
    <div className="min-h-screen relative">
      {/* Background Image with Overlay */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/lovable-uploads/c833140f-6f80-472e-ad4d-4fa59c444802.png')`,
          zIndex: -2
        }}
      />
      <div 
        className="fixed inset-0 bg-gradient-to-br from-soft-slate/70 via-sky-blue/60 to-soft-slate/70"
        style={{ zIndex: -1 }}
      />
      
      {/* Content Container */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary drop-shadow-lg">
            Shared Matter
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-foreground max-w-3xl mx-auto leading-relaxed">
            A memory-powered AI that actually knows you.
            Shared Matter helps you remember, plan, and automatically take action.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg" 
              className="text-lg px-8 py-6 rounded-full bg-gradient-to-r from-primary to-accent text-white hover:from-primary/90 hover:to-accent/90 transition-all shadow-lg hover:shadow-xl hover:scale-105 transform"
              onClick={onGetStarted}
            >
              Get Started
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="text-lg px-8 py-6 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all shadow-lg hover:shadow-xl hover:scale-105 transform bg-white/80 backdrop-blur-sm"
              onClick={onSignIn}
            >
              Sign In
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="glass-morphism p-8 rounded-2xl border border-border">
            <h3 className="text-2xl font-semibold mb-4 text-primary">Personalized AI with Real Memory</h3>
            <p className="text-foreground text-lg leading-relaxed">Your assistant remembers what you share: preferences, past chats, goals, and more. It gets smarter over time.</p>
          </div>

          <div className="glass-morphism p-8 rounded-2xl border border-border">
            <h3 className="text-2xl font-semibold mb-4 text-primary">Calendar-Savvy Assistant</h3>
            <p className="text-foreground text-lg leading-relaxed">Manage your Google or Outlook calendar via chat or voice. Book meetings, get proactive reminders, and never double-book again.</p>
          </div>

          <div className="glass-morphism p-8 rounded-2xl border border-border">
            <h3 className="text-2xl font-semibold mb-4 text-primary">Understands Your Docs</h3>
            <p className="text-foreground text-lg leading-relaxed">Upload documents, and your assistant can instantly answer questions about them. No reuploading or repeating needed.</p>
          </div>

          <div className="glass-morphism p-8 rounded-2xl border border-border">
            <h3 className="text-2xl font-semibold mb-4 text-primary">Proactive Slack Nudges</h3>
            <p className="text-foreground text-lg leading-relaxed">Get intelligent reminders, tips, or follow-ups from your AI in Slack without having to ask first.</p>
          </div>

          <div className="glass-morphism p-8 rounded-2xl border border-border">
            <h3 className="text-2xl font-semibold mb-4 text-primary">Ask Anything, Get Contextual Answers</h3>
            <p className="text-foreground text-lg leading-relaxed">Unlike ChatGPT, Shared Matter considers your past activity, schedule, and documents in its responses.</p>
          </div>

          <div className="glass-morphism p-8 rounded-2xl border border-border">
            <h3 className="text-2xl font-semibold mb-4 text-primary">Chat or Talk</h3>
            <p className="text-foreground text-lg leading-relaxed">Prefer to speak? Your assistant listens and responds naturally, just like a real conversation.</p>
          </div>

        </div>

        {/* Why Choose Shared Matter Section */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
            Why Choose Shared Matter
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <Brain className="w-16 h-16 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">Voice-First</h3>
              <p className="text-foreground">Natural conversation interface</p>
            </div>
            
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <Database className="w-16 h-16 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">Cross-Platform</h3>
              <p className="text-foreground">Works across all your apps</p>
            </div>
            
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <Shield className="w-16 h-16 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">Proactive</h3>
              <p className="text-foreground">Takes action without prompting</p>
            </div>
            
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <Zap className="w-16 h-16 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">Intelligent</h3>
              <p className="text-foreground">Learns and adapts to you</p>
            </div>
          </div>
        </div>

        <div className="text-center mb-16">
          <h2 className="text-3xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
            Stay Ahead, Not Overwhelmed
          </h2>
          <p className="text-xl text-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
            Whether you're managing complex projects, coordinating teams, or 
            simply trying to stay organized—Shared Matter handles the details 
            so you can focus on what matters most.
          </p>
          <Button 
            size="lg" 
            className="text-lg px-8 py-6 rounded-full bg-gradient-to-r from-primary to-accent text-white hover:from-primary/90 hover:to-accent/90 transition-all shadow-lg hover:shadow-xl hover:scale-105 transform"
            onClick={onGetStarted}
          >
            Start Your AI Assistant
          </Button>
        </div>

        {/* Footer with Privacy and Terms links */}
        <div className="text-center py-8 border-t border-border/20">
          <div className="flex justify-center items-center gap-6 text-sm text-muted-foreground">
            <Link 
              to="/privacy" 
              className="hover:text-primary transition-colors hover:underline"
            >
              Privacy Policy
            </Link>
            <span>•</span>
            <Link 
              to="/tos" 
              className="hover:text-primary transition-colors hover:underline"
            >
              Terms of Service
            </Link>
            <span>•</span>
            <span>&copy; {new Date().getFullYear()} K2 Innoventions</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
